import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactElement, ReactNode } from "react";

import { cn } from "../lib/cn";
import { REDUCED_MOTION_SAFE } from "../lib/focus";

export type typeTooltipProps = {
  /**
   * The tip. Keep it to a short phrase.
   *
   * NEVER put something a fan needs here. Most of this audience is on a phone
   * with no hover at all, so a tooltip is a nicety for desktop and admin
   * screens, not a place to hide a rule, a deadline, or an error.
   */
  content: string;
  /** The trigger. A single focusable element — a button, a link, an icon button. */
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left" | undefined;
  /** Milliseconds of hover before it opens. */
  delayDuration?: number | undefined;
  /** Renders the trigger without the tip, e.g. when the label is already visible. */
  disabled?: boolean | undefined;
  className?: string | undefined;
};

/**
 * A hover/focus tip on a Radix primitive.
 *
 * The provider is rendered inside this component rather than required at the
 * app root. Radix allows nesting, the cost is negligible, and the alternative —
 * a tooltip that silently renders nothing because someone forgot a provider —
 * is a bug nobody notices until a screenshot review.
 *
 * Opens on focus as well as hover, so a keyboard user gets the same tip.
 */
export const Tooltip = ({
  content,
  children,
  side = "top",
  delayDuration = 200,
  disabled = false,
  className,
}: typeTooltipProps): ReactNode => {
  if (disabled) return children;

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              // Inverted surface: near-black in light mode, near-white in dark.
              // `textInverse` on `text` is asserted at 4.5:1 in both modes.
              "z-[var(--sm-z-tooltip)] max-w-[16rem] rounded-sm bg-ink px-3 py-2",
              "text-caption text-ink-inverse shadow-md",
              "data-[state=delayed-open]:animate-popover-in data-[state=closed]:animate-popover-out",
              REDUCED_MOTION_SAFE,
              className,
            )}
          >
            {content}
            <TooltipPrimitive.Arrow
              className="fill-ink"
              width={10}
              height={5}
            />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};
